import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createContext } from 'react';
import type {
  CreateExpenseExportParams,
  CreateExpenseExportResponse,
  CreateExpenseParams,
  CreateExpenseResponse,
  DeleteExpenseParams,
  GetExpenseSubcategoriesResponse,
  GetExpensesParams,
  GetExpensesResponse,
  IExpensePhoenixApi,
  ScheduleExpensesForExportParams,
  ScheduleExpensesForExportResponse,
  UpdateExpenseParams,
  UpdateExpenseResponse,
} from './api';
import { ExpensePhoenixApi } from './api';
import {
  EXPENSE_SUBCATEGORIES_PHOENIX_KEY,
  EXPENSES_PHOENIX_KEY,
  EXPENSES_PHOENIX_MAIN_KEY,
} from './queryKeys';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';
import {
  CASH_LIST_TRANSACTIONS_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
} from '../cash-management-phoenix/queryKeys';
import { CASH_TRANSACTION_GROUP_LIST_KEY } from '../cash-management/queryKeys';

export type {
  CreateExpenseBody,
  CreateExpenseExportParams,
  CreateExpenseExportResponse,
  CreateExpenseParams,
  CreateExpenseResponse,
  DeleteExpenseParams,
  Expense,
  ExpenseCategory,
  ExpenseCashTransactionGroup,
  ExpenseExportSummary,
  ExpenseListMeta,
  ExpensePart,
  ExpensePartTax,
  ExpenseSubcategory,
  ExpenseSubcategorySummary,
  ExpenseTab,
  GetExpenseSubcategoriesResponse,
  GetExpensesParams,
  GetExpensesResponse,
  IExpensePhoenixApi,
  ScheduleExpensesForExportParams,
  ScheduleExpensesForExportResponse,
  UpdateExpenseBody,
  UpdateExpenseParams,
  UpdateExpensePart,
  UpdateExpensePartTax,
  UpdateExpenseResponse,
} from './api';
export { EXPENSE_CATEGORIES } from './api';

const ApiContext = createContext<IExpensePhoenixApi | undefined>(undefined);
const { useApi: useExpensePhoenixApi, ApiProvider: ExpensePhoenixApiProvider } =
  makeApiProvider({
    name: 'ExpensePhoenix',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new ExpensePhoenixApi(tenantId, auth, apiUrl),
  });

export { ExpensePhoenixApiProvider, useExpensePhoenixApi };

export function useExpenses(
  params: GetExpensesParams['params'],
  options?: Omit<
    UseQueryOptions<GetExpensesResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpensesResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSES_PHOENIX_KEY(params),
    (api) => {
      return api.getExpenses({ params });
    },
    {
      ...options,
      placeholderData: keepPreviousData,
    }
  );
}

export function useCreateExpense(): UseMutationResult<
  CreateExpenseResponse,
  unknown,
  CreateExpenseParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<CreateExpenseResponse, unknown, CreateExpenseParams>({
    mutationKey: ['expense-create'],
    mutationFn: (data) => api.createExpense(data),
    onSuccess: async (_response, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            variables.body.cash_list_transaction_group_id
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useUpdateExpense(): UseMutationResult<
  UpdateExpenseResponse,
  unknown,
  UpdateExpenseParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<UpdateExpenseResponse, unknown, UpdateExpenseParams>({
    mutationKey: ['expense-update'],
    mutationFn: (data) => api.updateExpense(data),
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            response.data.cash_list_transaction_group_id
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useScheduleExpensesForExport(): UseMutationResult<
  ScheduleExpensesForExportResponse,
  unknown,
  ScheduleExpensesForExportParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    ScheduleExpensesForExportResponse,
    unknown,
    ScheduleExpensesForExportParams
  >({
    mutationKey: ['expense-schedule-for-export'],
    mutationFn: (data) => api.scheduleExpensesForExport(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useCreateExpenseExport(): UseMutationResult<
  CreateExpenseExportResponse,
  unknown,
  CreateExpenseExportParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    CreateExpenseExportResponse,
    unknown,
    CreateExpenseExportParams
  >({
    mutationKey: ['expense-create-export'],
    mutationFn: (data) => api.createExpenseExport(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useUnscheduleExpensesForExport(): UseMutationResult<
  ScheduleExpensesForExportResponse,
  unknown,
  ScheduleExpensesForExportParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    ScheduleExpensesForExportResponse,
    unknown,
    ScheduleExpensesForExportParams
  >({
    mutationKey: ['expense-unschedule-for-export'],
    mutationFn: (data) => api.unscheduleExpensesForExport(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useDeleteExpense(): UseMutationResult<
  void,
  unknown,
  DeleteExpenseParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteExpenseParams>({
    mutationKey: ['expense-delete'],
    mutationFn: (data) => api.deleteExpense(data),
    onSuccess: async (_response, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            variables.params.cash_list_transaction_group_id
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useExpenseSubcategories(
  options?: Omit<
    UseQueryOptions<GetExpenseSubcategoriesResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpenseSubcategoriesResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSE_SUBCATEGORIES_PHOENIX_KEY,
    (api) => api.getExpenseSubcategories(),
    options
  );
}
